/**
 * Collection Filtering, Sorting, and Infinite Scroll
 *
 * Haalt volgende pagina's pas op wanneer de bezoeker in de buurt komt, met één
 * pagina als buffer. Daarvoor werd bij het laden van elke collectiepagina
 * meteen álles opgehaald: op /collections/alle-kamerplanten waren dat 22
 * pagina's van 671 KB, samen bijna 15 MB HTML die ook nog stuk voor stuk als
 * volledig document geparst werd. Dat was met 1.358 ms het zwaarste script op
 * de pagina.
 *
 * Twee wijzigingen:
 *  - lui laden in plaats van alles vooraf, met een buffer van één pagina zodat
 *    de eerste scroll nog steeds direct reageert;
 *  - ophalen via ?section_id=, zodat alleen de collectiesectie terugkomt
 *    (313 KB in plaats van 671 KB) en er geen compleet document geparst wordt.
 */
document.addEventListener('DOMContentLoaded', function() {
    const filterForm = document.querySelector('#filters');
    const sortSelects = document.querySelectorAll('.js-sort-by');
    const productGrid = document.querySelector('#products-grid');
    const infiniteScrollTrigger = document.querySelector('#infinite-scroll-trigger');
    const loadingIndicator = document.querySelector('#infinite-scroll-loading');
    const endMessage = document.querySelector('#infinite-scroll-end');

    // Exit early if required elements are not found
    if (!filterForm || sortSelects.length === 0 || !productGrid) {
        return;
    }

    // Initialize Shopify query params object if not exists
    if (typeof Shopify === 'undefined') {
        window.Shopify = {};
    }
    Shopify.queryParams = {};

    /**
     * GET met dezelfde vorm als axios.get: { data }, JSON geparsed wanneer de server
     * JSON stuurt, anders tekst, en een fout bij een status buiten 2xx.
     *
     * Deze pagina laadde axios apart van cdn.jsdelivr.net, zonder versie. Dat was
     * een extra verbinding naar een vreemde origin, 14 KB script, en elke dag
     * mogelijk een andere versie, voor iets wat fetch al kan.
     */
    const httpGet = async (url) => {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} voor ${url}`);
        }
        const text = await response.text();
        const type = response.headers.get('content-type') || '';
        return { data: type.includes('json') ? JSON.parse(text) : text };
    };

    // Preload state
    let currentPage = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.currentPage) : 1;
    let totalPages = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.totalPages) : 1;
    let preloadedProducts = [];
    let isPreloading = false;
    let preloadComplete = false;
    let revealObserver = null;
    // Eerstvolgende pagina die nog opgehaald moet worden.
    let nextPage = currentPage + 1;

    // Hoeveel kaarten er per keer zichtbaar worden, en de ondergrens waaronder
    // we alvast de volgende pagina ophalen.
    const REVEAL_BATCH = 24;
    // Hoeveel kaarten er klaar moeten staan. Bij het laden één lading, zodat de
    // pagina zelf niet hoeft te wachten op een tweede pagina HTML. Na de eerste
    // keer doorscrollen twee: wie verder scrolt dan het netwerk bijhoudt, ziet
    // anders de laadmelding en de collectietekst eronder in beeld staan op het
    // moment dat de nieuwe kaarten erboven verschijnen. Die sprong telt mee
    // voor CLS, want scrollen geldt niet als invoer.
    let bufferTarget = REVEAL_BATCH;
    // Hoe ver onder het scherm de trigger mag staan om de volgende lading al te
    // tonen. Alleen onder: boven het scherm hoeft niets te laden. Ruim genoeg
    // dat de nieuwe kaarten buiten beeld worden toegevoegd, ook bij snel scrollen.
    const REVEAL_MARGIN_PX = 1600;
    // De sectie die de productgrid rendert; hiermee vraagt Shopify alleen dat
    // stuk op in plaats van de hele pagina.
    const COLLECTION_SECTION_ID = 'single-collection';

    /**
     * Get query parameters from the current URL
     */
    const getQueryParams = () => {
        const queryParams = {};
        if (location.search.length) {
            const params = new URLSearchParams(location.search);
            params.forEach((value, key) => {
                if (queryParams[key]) {
                    queryParams[key] = [].concat(queryParams[key], value);
                } else {
                    queryParams[key] = value;
                }
            });
        }
        return queryParams;
    };

    Shopify.queryParams = getQueryParams();

    /**
     * Build URL with current filters, sorting, and page number
     */
    const buildUrlWithParams = (pageNum) => {
        const queryParams = { ...Shopify.queryParams };
        queryParams.page = pageNum;

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            if (Array.isArray(queryParams[key])) {
                queryParams[key].forEach(value => searchParams.append(key, value));
            } else {
                searchParams.append(key, queryParams[key]);
            }
        });

        return `${window.location.pathname}?${searchParams.toString()}`;
    };

    /**
     * De laadmelding houdt haar hoogte zolang er nog pagina's komen en wordt
     * alleen zichtbaar of onzichtbaar gemaakt. Met d-none groeide het blok onder
     * de grid bij elk ophalen een stuk, en kromp het weer: een verschuiving van
     * alles eronder. Pas als er niets meer komt valt de ruimte weg, in dezelfde
     * stap waarin de laatste kaarten erbij komen.
     *
     * De melding is alleen zichtbaar als de bezoeker echt wacht: de buffer is
     * leeg en de volgende pagina is onderweg. Bij ophalen op de achtergrond
     * bleef ze anders in beeld staan terwijl er kaarten uit de buffer boven
     * haar werden ingevoegd, en dan schoof de melding zelf zichtbaar weg.
     * preloadNextPage verbergt haar weer vóór de nieuwe kaarten erbij komen.
     */
    const showLoader = (visible) => {
        if (loadingIndicator) loadingIndicator.classList.toggle('is-loading', visible);
    };
    const collapseLoader = () => {
        if (!loadingIndicator) return;
        loadingIndicator.classList.remove('is-loading');
        loadingIndicator.classList.add('d-none');
    };

    /**
     * Preload all remaining pages in the background
     */
    const preloadNextPage = async () => {
        if (isPreloading) return;

        if (nextPage > totalPages) {
            preloadComplete = true;
            showLoader(false);
            return;
        }

        isPreloading = true;

        try {
            const url = buildUrlWithParams(nextPage) + '&section_id=' + COLLECTION_SECTION_ID;
            const response = await httpGet(url);
            const parser = new DOMParser();
            const doc = parser.parseFromString(response.data, 'text/html');

            const newProductsContainer = doc.querySelector('#products-grid');
            if (newProductsContainer) {
                const newProducts = newProductsContainer.querySelectorAll(':scope > div');
                newProducts.forEach(product => {
                    preloadedProducts.push(product.cloneNode(true));
                });
            }
            nextPage++;
        } catch (error) {
            console.error('Error preloading products:', error);
            // Ook bij een fout doortellen, anders blijft dezelfde pagina
            // eindeloos opnieuw geprobeerd worden.
            nextPage++;
        }

        isPreloading = false;
        if (nextPage > totalPages) {
            preloadComplete = true;
        }
        showLoader(false);

        // Stond de trigger al in beeld toen dit begon, dan komt er geen nieuwe
        // intersectie-melding meer; dan tonen we hier zelf. Dit stopt vanzelf
        // zodra er genoeg kaarten staan om de trigger uit beeld te duwen.
        if (preloadedProducts.length > 0 && triggerIsNear()) {
            revealMoreProducts();
        }
        // Na de eerste scroll staan er twee ladingen klaar; vul aan tot daar.
        topUpBuffer();
    };

    /**
     * Trustpilot-widgets in nieuw toegevoegde kaarten alsnog laten laden.
     *
     * tp.widget.bootstrap.min.js scant de pagina één keer bij het laden. Alles
     * wat daarna in de DOM komt blijft onaangeraakt staan: je ziet dan alleen
     * de terugvaltekst "Trustpilot" in plaats van de sterrenbalk. Bij het
     * doorscrollen van een collectie gebeurde dat vanaf de tweede lading.
     *
     * Dit stond ook al zo op het live thema - het is geen gevolg van de
     * omschakeling naar lui laden, maar het valt nu wel netjes op te lossen op
     * het moment dat we zelf kaarten toevoegen.
     *
     * Hetzelfde geldt wanneer sorteren, filteren of een pil de hele grid
     * vervangt: de trust-banner in de grid bleef dan als los woord "Trustpilot"
     * staan. Elke plek die de grid vervangt roept dit daarom ook aan.
     */
    const activeerTrustpilot = (elementen) => {
        // De loader in theme.liquid beslist per widget: dichtbij en zichtbaar wordt
        // getekend (en laadt zo nodig de bootstrap), de rest wacht. Zo komt een
        // verborgen variant ook niet in beeld als de bootstrap pas ná het vervangen
        // van de grid laadt en dan alles met de klasse trustpilot-widget tekent.
        const loader = window.gbTrustpilot && typeof window.gbTrustpilot.aanmelden === 'function'
            ? window.gbTrustpilot
            : null;
        if (!loader && (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function')) return;

        elementen.forEach(el => {
            if (!el || typeof el.querySelectorAll !== 'function') return;

            const widgets = el.matches && el.matches('.trustpilot-widget')
                ? [el]
                : Array.from(el.querySelectorAll('.trustpilot-widget'));

            widgets.forEach(widget => {
                // Al geïnitialiseerd? Dan heeft hij een iframe en laten we hem staan.
                if (widget.querySelector('iframe')) return;
                if (loader) {
                    loader.aanmelden(widget);
                    return;
                }
                // Verborgen variant (bijvoorbeeld de desktopbanner op mobiel): die
                // kreeg anders een lege iframe van hoogte 0 en een verzoek voor niets.
                if (widget.getClientRects().length === 0) return;
                try {
                    window.Trustpilot.loadFromElement(widget, true);
                } catch (e) {
                    // Widget niet kunnen laden is vervelend maar mag de rij niet breken.
                }
            });
        });
    };

    /**
     * Staat de scroll-trigger nu binnen (of vlak onder) het scherm?
     *
     * De IntersectionObserver meldt alleen een *verandering*. Na filteren of
     * sorteren staat de trigger al in beeld en vuurt hij dus één keer af —
     * precies terwijl de eerste pagina nog opgehaald wordt, waardoor die ene
     * melding verloren ging en er daarna nooit meer iets bijkwam. Daarom kijken
     * we na het ophalen zelf even waar de trigger staat.
     */
    const triggerIsNear = () => {
        if (!infiniteScrollTrigger) return false;
        const rect = infiniteScrollTrigger.getBoundingClientRect();
        return rect.top < window.innerHeight + REVEAL_MARGIN_PX;
    };

    /**
     * Zorgt dat er alvast kaarten klaarstaan (zie bufferTarget), zodat de
     * eerstvolgende scroll niet op het netwerk hoeft te wachten.
     */
    const topUpBuffer = () => {
        if (!preloadComplete && !isPreloading && preloadedProducts.length < bufferTarget) {
            preloadNextPage();
        }
    };

    /**
     * Reveal next batch of preloaded products
     */
    const revealMoreProducts = async () => {
        // Buffer leeg maar er zijn nog pagina's: eerst ophalen, dan tonen.
        if (preloadedProducts.length === 0 && !preloadComplete) {
            showLoader(true);
            await preloadNextPage();
        }

        const productsToAdd = preloadedProducts.splice(0, REVEAL_BATCH);

        if (productsToAdd.length === 0) {
            if (preloadComplete && preloadedProducts.length === 0) {
                if (endMessage) endMessage.classList.remove('d-none');
                collapseLoader();
                if (revealObserver) revealObserver.disconnect();
            }
            return;
        }

        productsToAdd.forEach(product => {
            productGrid.appendChild(product);
        });

        activeerTrustpilot(productsToAdd);

        // Check if all done
        if (preloadComplete && preloadedProducts.length === 0) {
            if (endMessage) endMessage.classList.remove('d-none');
            collapseLoader();
            if (revealObserver) revealObserver.disconnect();
        } else {
            // De bezoeker scrolt door: houd voortaan twee ladingen klaar.
            bufferTarget = 2 * REVEAL_BATCH;
            topUpBuffer();
        }
    };

    /**
     * Initialize scroll-based reveal
     */
    const initRevealOnScroll = () => {
        if (!infiniteScrollTrigger) return;

        if (totalPages <= 1) {
            if (endMessage) endMessage.classList.remove('d-none');
            return;
        }

        if (revealObserver) {
            revealObserver.disconnect();
        }

        revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // revealMoreProducts haalt zelf op wanneer er niets klaarstaat.
                // Staan er al kaarten klaar, dan tonen we die meteen, ook als
                // de volgende pagina nog onderweg is. Alleen met een lege buffer
                // wachten we: dan start isPreloading geen tweede fetch, en toont
                // preloadNextPage de kaarten zelf zodra ze binnen zijn.
                if (!entry.isIntersecting) return;
                if (preloadedProducts.length > 0 || !isPreloading) {
                    revealMoreProducts();
                } else {
                    // De bezoeker wacht op de pagina die al onderweg is.
                    showLoader(true);
                }
            });
        }, {
            root: null,
            rootMargin: `0px 0px ${REVEAL_MARGIN_PX}px 0px`,
            threshold: 0
        });

        revealObserver.observe(infiniteScrollTrigger);
    };

    /**
     * Reset state and preload products (used when filtering/sorting)
     */
    const resetAndPreload = (doc) => {
        const newTrigger = doc.querySelector('#infinite-scroll-trigger');
        if (newTrigger) {
            currentPage = parseInt(newTrigger.dataset.currentPage);
            totalPages = parseInt(newTrigger.dataset.totalPages);
        }

        preloadedProducts = [];
        isPreloading = false;
        preloadComplete = false;
        // Na filteren/sorteren begint de paginering opnieuw.
        nextPage = currentPage + 1;
        bufferTarget = REVEAL_BATCH;

        // Ruimte voor de laadmelding alleen houden als er nog pagina's komen.
        if (totalPages > 1) {
            if (loadingIndicator) loadingIndicator.classList.remove('d-none');
            showLoader(false);
        } else {
            collapseLoader();
        }

        if (endMessage) {
            if (totalPages <= 1) {
                endMessage.classList.remove('d-none');
            } else {
                endMessage.classList.add('d-none');
            }
        }

        initRevealOnScroll();
        topUpBuffer();
    };

    /**
     * Fetch ALL product IDs from a collection using Shopify JSON API
     * /collections/{handle}/products.json returns up to 250 per page
     */
    const collectionIdCache = {};
    const fetchCollectionProductIds = async (collectionHandle) => {
        // Return cached result if available
        if (collectionIdCache[collectionHandle]) return collectionIdCache[collectionHandle];

        const ids = new Set();
        let page = 1;

        while (true) {
            const rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
            const response = await httpGet(`${rootPath}collections/${collectionHandle}/products.json?limit=250&page=${page}`);
            const products = response.data.products;
            if (!products || products.length === 0) break;
            products.forEach(p => ids.add(String(p.id)));
            if (products.length < 250) break;
            page++;
        }

        collectionIdCache[collectionHandle] = ids;
        return ids;
    };

    /**
     * Fetch all pages of a collection's HTML and return matching product cards
     * Fetches page 1 first to get totalPages, then remaining pages in parallel
     */
    const fetchFilteredCards = async (collectionPath, targetIds) => {
        const matchingCards = [];

        // Fetch page 1 to get totalPages
        const separator = collectionPath.includes('?') ? '&' : '?';
        const firstResponse = await httpGet(`${collectionPath}${separator}page=1`);
        const firstDoc = new DOMParser().parseFromString(firstResponse.data, 'text/html');
        const firstGrid = firstDoc.querySelector('#products-grid');
        if (!firstGrid) return matchingCards;

        const trigger = firstDoc.querySelector('#infinite-scroll-trigger');
        const maxPages = trigger ? parseInt(trigger.dataset.totalPages) : 1;

        // Collect matches from page 1
        firstGrid.querySelectorAll(':scope > div').forEach(card => {
            const block = card.querySelector('[data-product-id]');
            if (block && targetIds.has(block.getAttribute('data-product-id'))) {
                matchingCards.push(card.cloneNode(true));
            }
        });

        // Fetch remaining pages in parallel
        if (maxPages > 1) {
            const pagePromises = [];
            for (let p = 2; p <= maxPages; p++) {
                pagePromises.push(httpGet(`${collectionPath}${collectionPath.includes('?') ? '&' : '?'}page=${p}`));
            }
            const responses = await Promise.all(pagePromises);
            responses.forEach(response => {
                const doc = new DOMParser().parseFromString(response.data, 'text/html');
                const grid = doc.querySelector('#products-grid');
                if (grid) {
                    grid.querySelectorAll(':scope > div').forEach(card => {
                        const block = card.querySelector('[data-product-id]');
                        if (block && targetIds.has(block.getAttribute('data-product-id'))) {
                            matchingCards.push(card.cloneNode(true));
                        }
                    });
                }
            });
        }

        return matchingCards;
    };

    // Track active collection pill for intersection filtering
    let activeCollectionPill = null;

    /**
     * Aantal producten uit de teller in de collectiekop van een opgehaalde
     * pagina. null als de pagina geen teller heeft; dan tonen we ook geen getal.
     */
    const readProductCount = (doc) => {
        const counter = doc.querySelector('.js-product-count-number');
        if (!counter) return null;
        const count = parseInt(counter.textContent.trim(), 10);
        return Number.isNaN(count) ? null : count;
    };

    /**
     * Update query parameters and reload filtered results.
     *
     * Geeft een promise terug met het aantal gevonden producten (of null), en
     * een afgewezen promise als het ophalen mislukt. Zo kan de knop
     * "Toepassen" wachten tot de producten er echt staan.
     */
    const updateParamsAndFilter = (resetPage = true) => {
        const queryParams = getQueryParams();
        const formData = new FormData(filterForm);

        Array.from(filterForm.elements).forEach(element => {
            if (element.name && queryParams[element.name]) {
                delete queryParams[element.name];
            }
        });

        formData.forEach((value, key) => {
            if (value) {
                if (queryParams[key]) {
                    queryParams[key] = [].concat(queryParams[key], value);
                } else {
                    queryParams[key] = value;
                }
            }
        });

        if (resetPage) {
            delete queryParams.page;
        }

        if (Shopify.queryParams.sort_by) {
            queryParams.sort_by = Shopify.queryParams.sort_by;
        }

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            if (Array.isArray(queryParams[key])) {
                queryParams[key].forEach(value => searchParams.append(key, value));
            } else {
                searchParams.append(key, queryParams[key]);
            }
        });

        const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

        // De URL pas aanpassen als de producten binnen zijn. Mislukt het ophalen,
        // dan blijft de oude lijst staan, en een URL met de nieuwe filters zou bij
        // herladen of delen een andere lijst tonen dan op het scherm stond.
        // Vóór resetAndPreload, want het oneindig scrollen leest Shopify.queryParams.
        const commitUrl = () => {
            window.history.pushState({}, '', urlWithParams);
            Shopify.queryParams = queryParams;
        };

        // If a collection pill is active, apply intersection filtering
        if (activeCollectionPill) {
            const filteredUrl = `${originalCollectionPath}?${searchParams.toString()}`;
            return fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
                const matchingCards = await fetchFilteredCards(filteredUrl, targetIds);
                commitUrl();

                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));
                activeerTrustpilot([productGrid]);

                const productCount = document.querySelector('.collection-product-count');
                if (productCount) {
                    const count = matchingCards.length;
                    const countNumbers = productCount.querySelectorAll('.js-product-count-number');
                    if (countNumbers.length) {
                        countNumbers.forEach(el => el.textContent = count);
                    } else {
                        productCount.textContent = count + (count === 1 ? ' product' : ' producten');
                    }
                }

                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;
                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');

                return matchingCards.length;
            });
        } else {
            return httpGet(urlWithParams)
                .then(response => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(response.data, 'text/html');
                    commitUrl();

                    productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;
                    activeerTrustpilot([productGrid]);

                    const resultsDiv = document.querySelector('#results');
                    const newResultsDiv = doc.querySelector('#results');
                    if (resultsDiv && newResultsDiv) {
                        resultsDiv.innerHTML = newResultsDiv.innerHTML;
                    }

                    const resultsDivMobile = document.querySelector('#results-mobile');
                    const newResultsDivMobile = doc.querySelector('#results-mobile');
                    if (resultsDivMobile && newResultsDivMobile) {
                        resultsDivMobile.innerHTML = newResultsDivMobile.innerHTML;
                    }

                    // Update product count in collection header
                    const productCount = document.querySelector('.collection-product-count');
                    const newProductCount = doc.querySelector('.collection-product-count');
                    if (productCount && newProductCount) {
                        productCount.innerHTML = newProductCount.innerHTML;
                    }

                    // Do NOT replace filter form content — filters are applied via "Apply" button
                    // and should remain as-is so users can adjust and re-apply.

                    resetAndPreload(doc);

                    return readProductCount(doc);
                });
        }
    };

    /**
     * Handle sort change
     */
    const handleSortChange = (sortValue) => {
        Shopify.queryParams.sort_by = sortValue;

        const queryParams = getQueryParams();
        queryParams.sort_by = sortValue;
        delete queryParams.page;

        Shopify.queryParams = queryParams;

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            const val = queryParams[key];
            if (Array.isArray(val)) {
                val.forEach(v => searchParams.append(key, v));
            } else {
                searchParams.append(key, String(val));
            }
        });

        const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

        window.history.pushState({}, '', urlWithParams);

        if (activeCollectionPill) {
            fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
                const matchingCards = await fetchFilteredCards(urlWithParams, targetIds);

                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));
                activeerTrustpilot([productGrid]);

                const productCount = document.querySelector('.collection-product-count');
                if (productCount) {
                    const count = matchingCards.length;
                    const countNumbers = productCount.querySelectorAll('.js-product-count-number');
                    if (countNumbers.length) {
                        countNumbers.forEach(el => el.textContent = count);
                    } else {
                        productCount.textContent = count + (count === 1 ? ' product' : ' producten');
                    }
                }

                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;
                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');
            }).catch(error => console.error('Error loading sorted products:', error));
        } else {
            httpGet(urlWithParams)
                .then(response => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(response.data, 'text/html');

                    productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;
                    activeerTrustpilot([productGrid]);

                    const resultsDiv = document.querySelector('#results');
                    const newResultsDiv = doc.querySelector('#results');
                    if (resultsDiv && newResultsDiv) {
                        resultsDiv.innerHTML = newResultsDiv.innerHTML;
                    }

                    const resultsDivMobile = document.querySelector('#results-mobile');
                    const newResultsDivMobile = doc.querySelector('#results-mobile');
                    if (resultsDivMobile && newResultsDivMobile) {
                        resultsDivMobile.innerHTML = newResultsDivMobile.innerHTML;
                    }

                    // Update product count in collection header
                    const productCount = document.querySelector('.collection-product-count');
                    const newProductCount = doc.querySelector('.collection-product-count');
                    if (productCount && newProductCount) {
                        productCount.innerHTML = newProductCount.innerHTML;
                    }

                    resetAndPreload(doc);
                })
                .catch(error => console.error('Error loading sorted products:', error));
        }
    };

    /**
     * Terugknop: plek in de lijst bewaren.
     *
     * Komt de bezoeker via de terugknop terug en haalt de browser de pagina niet
     * uit de back/forward-cache, dan laadt Shopify alleen de eerste pagina. De
     * bijgeladen producten waren dan weg en de bezoeker stond weer bovenaan, ook
     * als hij 7.000 px diep zat. Bij het verlaten bewaren we daarom hoeveel
     * kaarten er stonden en waar er gescrold was; bij een terugnavigatie naar
     * precies dezelfde URL laden we die kaarten opnieuw en scrollen terug.
     * Uit de back/forward-cache staat alles er al; dan doen we niets.
     */
    const RETURN_KEY = 'gb-collection-return';
    const RETURN_MAX_AGE_MS = 30 * 60 * 1000;
    const returnUrl = () => window.location.pathname + window.location.search;

    window.addEventListener('pagehide', () => {
        // De scrollpositie zetten we zelf terug, ná het bijladen. Laat de browser
        // het doen, dan zet hij de bezoeker onderaan de nog korte pagina; daar
        // laadt de lijst dan door tot het einde en blijft hij onderaan hangen.
        // Dit moet vóór het verlaten: de modus hoort bij deze historie-entry.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        try {
            sessionStorage.setItem(RETURN_KEY, JSON.stringify({
                url: returnUrl(),
                cards: productGrid.children.length,
                scrollY: window.scrollY,
                at: Date.now()
            }));
        } catch (e) {
            // Geen sessionStorage (privévenster, vol): dan zonder herstel.
        }
    });

    const readReturnState = () => {
        let saved = null;
        try {
            saved = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
            sessionStorage.removeItem(RETURN_KEY);
        } catch (e) {
            return null;
        }
        if (!saved || saved.url !== returnUrl()) return null;
        if (Date.now() - saved.at > RETURN_MAX_AGE_MS) return null;
        const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        if (!navigation || navigation.type !== 'back_forward') return null;
        return saved;
    };

    const restoreReturnState = async (saved) => {
        bufferTarget = 2 * REVEAL_BATCH;
        const deadline = Date.now() + 15000;
        while (productGrid.children.length < saved.cards && !(preloadComplete && preloadedProducts.length === 0)) {
            if (Date.now() > deadline) break;
            // Een pagina die al onderweg is eerst afwachten: revealMoreProducts
            // start geen tweede ophaalactie en zou dan niets toevoegen.
            if (preloadedProducts.length === 0 && isPreloading) {
                await new Promise(resolve => setTimeout(resolve, 50));
                continue;
            }
            const before = productGrid.children.length;
            await revealMoreProducts();
            if (productGrid.children.length === before) break;
        }
        // Pas scrollen nadat de laatste kaarten getekend zijn. In hetzelfde frame
        // telt Chrome het tekstblok onder de grid als verschoven: het zou op de
        // nieuwe scrollpositie in beeld hebben gestaan als de kaarten er niet bij
        // waren gekomen (gemeten: CLS 0,77).
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        window.scrollTo(0, saved.scrollY);
    };

    // Initialize
    initRevealOnScroll();

    const returnState = readReturnState();
    if (returnState) {
        restoreReturnState(returnState);
    } else {
        // De eerste buffer pas vullen wanneer de browser niets dringenders te doen
        // heeft. Meteen beginnen kostte ruim een seconde op de hoofdthread, precies
        // in het venster waarin de pagina zichtbaar en klikbaar moet worden.
        const warmUpBuffer = () => topUpBuffer();
        if ('requestIdleCallback' in window) {
            requestIdleCallback(warmUpBuffer, { timeout: 3000 });
        } else {
            setTimeout(warmUpBuffer, 1500);
        }
    }

    // Event listeners

    // Quick filter pills and collection pills — single-select across both types
    const quickFilterPills = document.querySelectorAll('.js-quick-filter');
    const collectionPills = document.querySelectorAll('.js-collection-pill');
    const originalCollectionPath = window.location.pathname;

    /**
     * Deactivate all pills (both quick filters and collection pills)
     */
    const deactivateAllPills = () => {
        quickFilterPills.forEach(p => p.classList.remove('is-active'));
        collectionPills.forEach(p => p.classList.remove('is-active'));
        activeCollectionPill = null;
    };

    /**
     * Zet de vinkjes en velden in de filterlade gelijk aan de URL.
     *
     * Een pil vervangt de URL, maar de lade werd niet bijgewerkt. Een filter die
     * de pil had weggehaald bleef daardoor aangevinkt staan, en kwam terug zodra
     * iemand daarna op Toepassen klikte ("de filter gaat niet uit"). Dat stond
     * ook zo op het live thema.
     */
    const syncFilterFormToUrl = () => {
        const params = new URLSearchParams(location.search);
        Array.from(filterForm.elements).forEach(element => {
            if (!element.name) return;
            if (element.type === 'checkbox' || element.type === 'radio') {
                element.checked = params.getAll(element.name).includes(element.value);
            } else if (element.tagName === 'INPUT' && element.type === 'text') {
                const value = params.get(element.name) || '';
                if (element.value !== value) {
                    element.value = value;
                    // De prijsschuif luistert naar input op deze velden.
                    element.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        });
    };

    /**
     * Pad van de collectie met alleen de huidige sortering. Een pil liet die
     * weg: het label bleef "Prijs: laag naar hoog" zeggen terwijl de producten
     * niet meer op volgorde stonden.
     */
    const collectionPathWithSort = (params = new URLSearchParams()) => {
        const sortBy = Shopify.queryParams.sort_by;
        if (sortBy) params.set('sort_by', sortBy);
        const query = params.toString();
        return query ? `${originalCollectionPath}?${query}` : originalCollectionPath;
    };

    /**
     * Fetch URL via AJAX and update product grid
     */
    const fetchAndUpdateGrid = (url) => {
        window.history.pushState({}, '', url);
        Shopify.queryParams = getQueryParams();
        syncFilterFormToUrl();

        httpGet(url)
            .then(response => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(response.data, 'text/html');

                productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;
                activeerTrustpilot([productGrid]);

                const productCount = document.querySelector('.collection-product-count');
                const newProductCount = doc.querySelector('.collection-product-count');
                if (productCount && newProductCount) {
                    productCount.innerHTML = newProductCount.innerHTML;
                }

                resetAndPreload(doc);

                // Scroll to top after filter is applied
                window.scrollTo({ top: 0, behavior: 'smooth' });
            })
            .catch(error => console.error('Error applying filter:', error));
    };

    // Quick filter pills — metafield-based filters
    quickFilterPills.forEach(pill => {
        pill.addEventListener('click', function(e) {
            e.preventDefault();
            const param = this.dataset.param;
            const value = this.dataset.value;
            const isActive = this.classList.contains('is-active');

            // Deactivate ALL pills (both types)
            deactivateAllPills();

            if (isActive) {
                // Deselecting — go back to original collection with no filters
                fetchAndUpdateGrid(collectionPathWithSort());
            } else {
                // Selecting — clean URL with only this filter on original collection
                this.classList.add('is-active');
                const params = new URLSearchParams();
                params.set(param, value);
                fetchAndUpdateGrid(collectionPathWithSort(params));
            }
        });
    });

    // Collection pills — intersection: show current collection products that also exist in target collection
    collectionPills.forEach(pill => {
        pill.addEventListener('click', async function(e) {
            e.preventDefault();
            const targetHandle = this.dataset.collectionHandle;
            const targetUrl = this.dataset.collectionUrl;
            const isActive = this.classList.contains('is-active');

            // Deactivate ALL pills (both types)
            deactivateAllPills();

            if (isActive) {
                // Deselecting — go back to original collection
                activeCollectionPill = null;
                fetchAndUpdateGrid(collectionPathWithSort());
            } else {
                this.classList.add('is-active');
                activeCollectionPill = targetHandle;

                // De pil toont de doorsnede zonder filters uit de lade. Zet de URL
                // en de lade daarop, anders kwamen die filters bij de volgende
                // Toepassen stil terug. De sortering blijft staan.
                const pillPath = collectionPathWithSort();
                window.history.pushState({}, '', pillPath);
                Shopify.queryParams = getQueryParams();
                syncFilterFormToUrl();

                // Stop infinite scroll while we work
                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;

                // Fetch target IDs using default-language handle (JSON API always uses default handles)
                const targetIds = await fetchCollectionProductIds(targetHandle);
                const matchingCards = await fetchFilteredCards(pillPath, targetIds);

                // Replace grid with only matching products
                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));
                activeerTrustpilot([productGrid]);

                // Update count
                const productCount = document.querySelector('.collection-product-count');
                if (productCount) {
                    const count = matchingCards.length;
                    const countNumbers = productCount.querySelectorAll('.js-product-count-number');
                    if (countNumbers.length) {
                        countNumbers.forEach(el => el.textContent = count);
                    } else {
                        productCount.textContent = count + (count === 1 ? ' product' : ' producten');
                    }
                }

                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');

                // Scroll to top after filter is applied
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    });

    // Filters are NOT applied on change — only when "Apply" button is clicked.
    //
    // Het paneel bleef niet open staan tot de producten er waren: het sloot
    // meteen en de grid werd pas daarna, zonder zichtbaar signaal, ververst.
    // Op mobiel paste 29% van de sessies die een filter toepasten dat twee keer
    // of vaker toe (PostHog, 23-29 september 2026). Nu toont de knop dat hij
    // bezig is, sluit het paneel pas als de producten er staan en meldt een
    // korte melding hoeveel producten er gevonden zijn.
    const applyFilterBtns = document.querySelectorAll('.js-filter-apply');
    const applyError = document.querySelector('.js-filter-apply-error');
    const resultsToast = document.querySelector('.js-filter-results-toast');
    const resultsStatus = document.querySelector('.js-filter-results-status');
    let toastTimer = null;

    const setApplyBusy = (btn, busy) => {
        const label = btn.querySelector('.js-filter-apply-label');
        if (label) {
            if (!btn.dataset.labelIdle) btn.dataset.labelIdle = label.textContent;
            label.textContent = busy ? (btn.dataset.labelBusy || btn.dataset.labelIdle) : btn.dataset.labelIdle;
        }
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    };

    const resultsText = (count) => {
        if (!resultsToast || count === null) return '';
        if (count === 0) return resultsToast.dataset.textNone || '';
        if (count === 1) return resultsToast.dataset.textOne || '';
        return (resultsToast.dataset.textOther || '').replace('__TOTAL__', count);
    };

    const showResultsCount = (count) => {
        const text = resultsText(count);
        if (!text) return;

        if (resultsStatus) resultsStatus.textContent = text;
        if (!resultsToast) return;

        resultsToast.textContent = text;
        resultsToast.classList.add('is-visible');
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => resultsToast.classList.remove('is-visible'), 3000);
    };

    // Sluit het paneel via toggles.js zonder een klik op het kruisje na te
    // bootsen. Die nagebootste klik kwam in PostHog binnen als een derde klik
    // binnen een seconde en telde zo als rage click die niemand gaf.
    const closeFilterDrawer = () => {
        document.dispatchEvent(new CustomEvent('gb:filter-drawer-close'));
    };

    applyFilterBtns.forEach(btn => {
        btn.addEventListener('click', function(e) {
            e.preventDefault();
            if (btn.getAttribute('aria-busy') === 'true') return;

            if (applyError) applyError.classList.add('d-none');
            setApplyBusy(btn, true);

            updateParamsAndFilter(true)
                .then(count => {
                    setApplyBusy(btn, false);
                    closeFilterDrawer();
                    // Scroll to top after filter is applied
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    showResultsCount(count);
                })
                .catch(error => {
                    console.error('Error loading filtered products:', error);
                    setApplyBusy(btn, false);
                    // Paneel open laten: de keuze staat er nog, dus opnieuw
                    // proberen is één tik.
                    if (applyError) applyError.classList.remove('d-none');
                });
        });
    });

    sortSelects.forEach(sortSelect => {
        sortSelect.addEventListener('change', function(e) {
            const newValue = e.target.value;
            sortSelects.forEach(select => {
                if (select !== e.target) {
                    select.value = newValue;
                }
            });
            handleSortChange(newValue);
            // Scroll to top after sorting
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    });

    // Mobile filter bar sticky positioning — keep below header
    const mobileFilterBar = document.querySelector('.js-mobile-filter-bar');
    if (mobileFilterBar) {
        const barRect = mobileFilterBar.getBoundingClientRect();
        const barOriginalTop = barRect.top + window.scrollY;

        const updateMobileBarTop = () => {
            const header = document.querySelector('.header');
            const headerMain = header ? header.querySelector('.header-main') : null;
            if (header && header.classList.contains('is-sticky') && headerMain) {
                mobileFilterBar.style.top = headerMain.offsetHeight + 'px';
            } else {
                mobileFilterBar.style.top = '0px';
            }

            // Toggle is-sticky class when bar is stuck
            const currentRect = mobileFilterBar.getBoundingClientRect();
            const stickyTop = parseFloat(mobileFilterBar.style.top) || 0;
            if (currentRect.top <= stickyTop + 1) {
                mobileFilterBar.classList.add('is-sticky');
            } else {
                mobileFilterBar.classList.remove('is-sticky');
            }
        };
        updateMobileBarTop();
        window.addEventListener('scroll', updateMobileBarTop, { passive: true });
        window.addEventListener('resize', updateMobileBarTop, { passive: true });
    }
});
